// File: src/components/AuthPage.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
// Ensure a reliable default cover in previews (centralized)
import { DEFAULT_COVER } from '@/constants/media';
import { REMOTE_FALLBACK_COVER_URL } from '../constants/images';
const DEFAULT_COVER_URL = DEFAULT_COVER;
import safeToast from '../utils/safeToast';
import ProgressBar from './ProgressBar';
// ...existing imports...
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { UsernameField } from "./auth/UsernameField";
import { LRU } from "../utils/lru";
// New atomic signup + username check helpers
import { checkUsername, suggestUsernames, signupAtomic } from '../services/api';
import { requestEmailOtp, verifyEmailOtp } from '../api/auth';
import { usePublicAuthSettings } from '../hooks/usePublicAuthSettings';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Building,
  MapPin,
  Calendar,
  Shield,
  Eye,
  EyeOff,
  Image,
  Lock,
  CheckCircle,
  Star,
  Sparkles,
  RefreshCw,
  Smartphone,
} from 'lucide-react';
import { countries, cities, cityCountryMap } from '../data/mockData';

interface AuthPageProps {
  authType: 'admin' | 'user';
  initialView?: 'login' | 'signup';
  embedded?: boolean;
  compact?: boolean;
  signupOnly?: boolean;
  hideExit?: boolean;
  /** CRM signup: account → payment (no template step messaging). */
  crmFlow?: boolean;
  backLabel?: string;
  onBack: () => void;
  onAdminLogin: (credentials: { email: string; password: string }, otpProof?: string) => void;
  onUserSignup: (userData: UserSignupData) => void;
  onUserSignin?: (credentials: { email: string; password: string }) => void;
  isAdminCreated?: boolean;
  authSettings?: any;
}

interface UserSignupData {
  name: string;
  email: string;
  phone: string;
  position: string;
  company: string;
  /** CRM signup company category (sales | general | cleaning | security | technical) */
  crmCategory?: string;
  gender: string;
  age: string;
  location: string;
  avatar: string;
  coverImage: string;
  password: string;
  bio: string;
  username?: string;
}

// Local signup data shape (includes handle + cardSlug)
type SignupData = {
  name: string;
  email: string;
  password: string;
  username: string; // handle/subdomain
  cardSlug: string; // per-card username
};

export const AuthPage: React.FC<AuthPageProps> = ({
  authType,
  initialView = 'login',
  embedded = false,
  compact = false,
  signupOnly = false,
  hideExit = false,
  crmFlow = false,
  backLabel,
  onBack,
  onAdminLogin,
  onUserSignup,
  onUserSignin = () => {},
  isAdminCreated = false,
  authSettings,
}) => {
  const { t } = useTranslation();
  const { adminTwoFaEnabled, adminCaptchaEnabled } = usePublicAuthSettings();

  // Always start on "login" view by default, but we may restore from saved signup progress
  const [isLogin, setIsLogin] = useState<boolean>(signupOnly ? false : initialView !== 'signup');

  const [localAuthSettings, setLocalAuthSettings] = useState<any>(authSettings);
  useEffect(() => {
    setLocalAuthSettings(authSettings);
  }, [authSettings]);

  useEffect(() => {
    setIsLogin(signupOnly ? false : initialView !== 'signup');
  }, [initialView, signupOnly]);

  const [showPassword, setShowPassword] = useState<boolean>(false);

  // Admin security states
  const [captchaCode, setCaptchaCode] = useState<string>(''); // admin input
  const [generatedCaptcha, setGeneratedCaptcha] = useState<string>(''); // admin generated
  const [showTwoFA, setShowTwoFA] = useState<boolean>(false);
  const [twoFACode, setTwoFACode] = useState<string>(''); // user input
  const [isVerifyingTwoFA, setIsVerifyingTwoFA] = useState<boolean>(false);

  // (moved below userForm to avoid TS “used before declaration” in effects)

  // User signin security states (CAPTCHA)
  const [userGeneratedCaptcha, setUserGeneratedCaptcha] = useState<string>(''); // shown code
  const [userCaptchaInput, setUserCaptchaInput] = useState<string>(''); // user input (FIXED)

  // Admin form state
  const [adminForm, setAdminForm] = useState({
    email: '',
    password: '',
  });

  // User signin form state
  const [userSigninForm, setUserSigninForm] = useState({
    email: '',
    password: '',
  });

  // Default empty signup form template
  const DEFAULT_USER_FORM: UserSignupData = {
    name: '',
    email: '',
    phone: '',
    position: '',
    company: '',
    crmCategory: '',
    gender: '',
    age: '',
    location: '',
    avatar: '',
    coverImage: '',
    password: '',
  bio: '',
  username: '',
  };

  // User signup form state (rehydrated from localStorage if available)
  const SIGNUP_PROGRESS_KEY = 'signup_progress_v1';
  const [userForm, setUserForm] = useState<UserSignupData>(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(SIGNUP_PROGRESS_KEY) : null;
      if (raw) {
        const parsed = JSON.parse(raw);
        return { ...DEFAULT_USER_FORM, ...(parsed?.userForm || {}) } as UserSignupData;
      }
    } catch {}
    return DEFAULT_USER_FORM;
  });
  // Location autocomplete state (for user signup)
  const [showLocationDropdown, setShowLocationDropdown] = useState<boolean>(false);
  // Initialize from persisted progress directly to avoid ordering issues
  const [locationInput, setLocationInput] = useState<string>(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem('signup_progress_v1') : null;
      if (raw) {
        const parsed = JSON.parse(raw);
        const loc = parsed?.userForm?.location as string | undefined;
        if (typeof loc === 'string') {
          const parts = String(loc).split(',').map((p) => p.trim()).filter(Boolean);
          // Return country (last token) if present
          if (parts.length >= 2) return parts[parts.length - 1];
          return loc;
        }
      }
    } catch {}
    return '';
  });
  // City input for finer granularity, composed with country into userForm.location
  const [cityInput, setCityInput] = useState<string>(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem('signup_progress_v1') : null;
      if (raw) {
        const parsed = JSON.parse(raw);
        const loc = parsed?.userForm?.location as string | undefined;
        if (typeof loc === 'string') {
          const parts = String(loc).split(',').map((p) => p.trim()).filter(Boolean);
          if (parts.length >= 2) return parts[0];
        }
      }
    } catch {}
    return '';
  });
  const [filteredCountries, setFilteredCountries] = useState<string[]>([]);
  const [showCityDropdown, setShowCityDropdown] = useState<boolean>(false);
  const [filteredCities, setFilteredCities] = useState<string[]>([]);

  // Derived validation: ensure city matches country when both are recognized
  const normalizedCity = String(cityInput || '').trim().toLowerCase();
  const normalizedCountry = String(locationInput || '').trim().toLowerCase();
  const expectedCountry = cityCountryMap[normalizedCity];
  const isKnownCity = !!expectedCountry;
  const isKnownCountry = countries.some((c) => c.toLowerCase() === normalizedCountry);
  const cityCountryMismatch = Boolean(
    normalizedCity && normalizedCountry && isKnownCity && isKnownCountry && expectedCountry.toLowerCase() !== normalizedCountry
  );
  // Signup-specific state (handle + cardSlug)
  const [signupForm, setSignupForm] = useState<SignupData>({
    name: '',
    email: '',
    password: '',
    username: '',
    cardSlug: '',
  });
  const [avatarPreview, setAvatarPreview] = useState<string>(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(SIGNUP_PROGRESS_KEY) : null;
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed?.avatarPreview || '';
      }
    } catch {}
    return '';
  });
  const [coverPreview, setCoverPreview] = useState<string>(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(SIGNUP_PROGRESS_KEY) : null;
      if (raw) {
        const parsed = JSON.parse(raw);
        return parsed?.coverPreview || DEFAULT_COVER_URL;
      }
    } catch {}
    return DEFAULT_COVER_URL;
  });
  const [avatarUploadProgress, setAvatarUploadProgress] = useState<number>(0);
  const [coverUploadProgress, setCoverUploadProgress] = useState<number>(0);
  // UX: submitting state for signup
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Ensure a visible cover when user hasn't uploaded one: preload default and fallback to remote
  React.useEffect(() => {
    // Only enforce when user has not picked a custom cover
    const noUserCover = !userForm.coverImage || String(userForm.coverImage).trim() === '';
    if (!noUserCover) return;
    // If preview already points to a known good default or remote fallback, nothing to do
    if (coverPreview && (coverPreview === DEFAULT_COVER_URL || coverPreview === REMOTE_FALLBACK_COVER_URL)) return;

  const probe = document.createElement('img') as HTMLImageElement;
    probe.onload = () => setCoverPreview(DEFAULT_COVER_URL);
    probe.onerror = () => setCoverPreview(REMOTE_FALLBACK_COVER_URL);
    probe.src = DEFAULT_COVER_URL;
    // No cleanup required; lightweight
  }, [userForm.coverImage]);

  // Username availability UX state (debounce + abort + cache)
  const [usernameOk, setUsernameOk] = React.useState<boolean | null>(null);
  const [usernameLoading, setUsernameLoading] = React.useState<boolean>(false);
  const [usernameSuggestions, setUsernameSuggestions] = React.useState<{ value: string }[]>([]);
  const [usernameSuggested, setUsernameSuggested] = React.useState<string>("");
  const [usernameAutofilled, setUsernameAutofilled] = React.useState<boolean>(false);
  const [isTypingUsername, setIsTypingUsername] = React.useState<boolean>(false);
  const usernameAbortRef = React.useRef<AbortController | null>(null);
  const usernameCache = React.useMemo(() => new LRU<string, { ok: boolean; suggestions: string[] }>(200), []);
  const debouncedUsername = useDebouncedValue(userForm.username || "", 250);

  // Default cover image (centralized)
  const defaultCoverImage = DEFAULT_COVER_URL;

  // Generate CAPTCHA on component mount / when switching relevant views
  React.useEffect(() => {
    if (authType === 'admin') {
      generateAdminCaptcha();
    } else if (authType === 'user' && isLogin) {
      generateUserCaptcha();
    }
    // When switching to the signup view (user and not login), reset the form to empty
    // BUT if we have saved progress, keep it.
    if (authType === 'user' && !isLogin) {
      const hasProgress = (() => {
        try { return !!window.localStorage.getItem(SIGNUP_PROGRESS_KEY); } catch { return false; }
      })();
      if (hasProgress) return;
      // Reset in-memory form and UI state
      setUserForm(DEFAULT_USER_FORM);
      setAvatarPreview('');
  setCoverPreview(DEFAULT_COVER_URL);
      setAvatarLastError(null);
      setCoverLastError(null);
      setAvatarUploadProgress(0);
      setCoverUploadProgress(0);
      // reset file input cancel refs
      avatarCancelRef.current = () => {};
      coverCancelRef.current = () => {};

      // Also clear relevant localStorage keys so previous sessions don't prefill signup fields
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.removeItem('CURRENT_USER');
          window.localStorage.removeItem('users');
          // dev tokens that might cause admin/user context
          window.localStorage.removeItem('dev_admin_token');
          window.localStorage.removeItem('dev_admin_role');
        }
      } catch (e) {
        console.warn('Failed to clear persisted signup keys', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authType, isLogin]);

  // If we restored progress and the form has an email/name, ensure we're on signup view
  React.useEffect(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(SIGNUP_PROGRESS_KEY) : null;
      if (raw) {
        const parsed = JSON.parse(raw);
        const uf = parsed?.userForm || {};
        if (authType === 'user' && (uf?.email || uf?.name)) {
          setIsLogin(false);
        }
      }
    } catch {}
  // run on mount only
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist signup progress on every relevant change
  React.useEffect(() => {
    try {
      const payload = { userForm, avatarPreview, coverPreview };
      if (authType === 'user') window.localStorage.setItem(SIGNUP_PROGRESS_KEY, JSON.stringify(payload));
    } catch {}
  }, [authType, userForm, avatarPreview, coverPreview]);

  // ----- Captcha helpers -----
  const randomCode = (len: number) => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // avoid ambiguous 0/O/1/I
    let result = '';
    for (let i = 0; i < len; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
    return result;
  };

  const generateAdminCaptcha = () => {
    setGeneratedCaptcha(randomCode(6));
    setCaptchaCode('');
  };

  const generateUserCaptcha = () => {
    setUserGeneratedCaptcha(randomCode(5));
    setUserCaptchaInput('');
  };

  // ----- 2FA helpers (adminTwoFaEnabled / adminCaptchaEnabled from server via usePublicAuthSettings) -----

  const sendTwoFACode = async (email: string): Promise<boolean> => {
    try {
      const r = await requestEmailOtp(email, 'admin_login');
      if (r.cooldown && r.cooldown > 0) {
        safeToast('warning', `Please wait ${r.cooldown}s before requesting another code.`);
        return false;
      }
      if (r.sent === false) {
        safeToast('error', 'Could not send the verification email. Check server email settings and try again.');
        return false;
      }
      safeToast('success', `2FA code sent to ${email}. Check your inbox.`);
      return true;
    } catch {
      safeToast('error', 'Failed to send 2FA code. Please try again in a moment.');
      return false;
    }
  };

  // ----- Upload helpers -----
  // Allow larger uploads client-side to match server's MAX_ACCEPT_BYTES (configurable via Vite env)
  const envMax = Number(import.meta.env.VITE_MAX_ACCEPT_BYTES || 20 * 1024 * 1024);
  const MAX_FRONTEND_UPLOAD_BYTES = envMax; // default 20MB
  const ALLOWED_FRONTEND_MIMES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml', 'image/gif'];

  const validateFile = (file: File) => {
    if (!ALLOWED_FRONTEND_MIMES.includes(file.type)) return { ok: false, error: 'Unsupported file type' };
    if (file.size > MAX_FRONTEND_UPLOAD_BYTES) return { ok: false, error: `File too large (max ${(MAX_FRONTEND_UPLOAD_BYTES/1024/1024).toFixed(0)}MB)` };
    return { ok: true };
  };

  // returns an object with promise and cancel() to abort
  const uploadWithProgress = (file: File, onProgress: (p: number) => void) => {
    const validation = validateFile(file);
    if (!validation.ok) {
      const ext = String(file.name || '').split('.').pop()?.toLowerCase() || '';
      const extOk = ['png', 'jpg', 'jpeg', 'webp', 'svg', 'gif'].includes(ext);
      if (!file.type && extOk && file.size <= MAX_FRONTEND_UPLOAD_BYTES) {
        // Mobile browsers often omit MIME type; allow known image extensions through.
      } else {
        return { promise: Promise.reject(new Error(validation.error)), cancel: () => {} };
      }
    }

    let xhr: XMLHttpRequest | null = new XMLHttpRequest();
    const promise = new Promise<string>((resolve, reject) => {
      try {
  // Relative URL keeps uploads same-origin in production (avoids CORS failures).
  const endpoint = '/api/uploads/image';
  xhr!.open('POST', endpoint);
  xhr!.withCredentials = true;
        xhr!.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            const percent = Math.round((ev.loaded / ev.total) * 100);
            onProgress(percent);
          }
        };
        xhr!.onload = () => {
          if (!xhr) return reject(new Error('Upload aborted'));
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              resolve(data.url);
            } catch (e) {
              reject(new Error('Invalid upload response'));
            }
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };
        xhr!.onerror = () => reject(new Error('Upload failed'));
        const form = new FormData();
        form.append('image', file, file.name);
        xhr!.send(form);
      } catch (e) {
        reject(e);
      }
    });

    return {
      promise,
      cancel: () => {
        if (xhr) {
          try { xhr.abort(); } catch (e) {}
          xhr = null;
        }
      },
    };
  };

  // track last failed error to allow retry
  const [avatarLastError, setAvatarLastError] = useState<string | null>(null);
  const [coverLastError, setCoverLastError] = useState<string | null>(null);
  // track cancel functions
  const avatarCancelRef = React.useRef<() => void>(() => {});
  const coverCancelRef = React.useRef<() => void>(() => {});

  const handleImageUpload = async (type: 'avatar' | 'cover', file: File | null) => {
    if (!file) return;
    try {
      if (type === 'avatar') {
        setAvatarUploadProgress(0);
        setAvatarLastError(null);
      } else {
        setCoverUploadProgress(0);
        setCoverLastError(null);
      }

      const { promise, cancel } = uploadWithProgress(file, (p) => {
        if (type === 'avatar') setAvatarUploadProgress(p); else setCoverUploadProgress(p);
      });
      if (type === 'avatar') avatarCancelRef.current = cancel; else coverCancelRef.current = cancel;

      const url = await promise;
      // normalize relative server urls (/uploads/...) to absolute using VITE_APP_BASE_URL or VITE_API_BASE_URL
      const envBase = import.meta.env.VITE_APP_BASE_URL || import.meta.env.VITE_API_BASE_URL;
      const base = envBase ? String(envBase).replace(/\/$/, '') : 'http://localhost:4000';
      const finalUrl = String(url).startsWith('/') ? `${base}${url}` : url;

      // Debug: attempt a fetch to the image URL and log status/content-type to help diagnose preview failures
      try {
        fetch(finalUrl, { method: 'GET' })
          .then((resp) => {
            console.log('Preview fetch', finalUrl, 'status', resp.status, 'content-type', resp.headers.get('content-type'));
          })
          .catch((err) => {
            console.warn('Preview fetch failed', finalUrl, err);
          });
      } catch (err) {
        console.warn('Preview fetch exception', err);
      }

      // Use an Image instance to validate load success/failure before setting the visible preview state
      const tester = document.createElement('img') as HTMLImageElement;
      tester.onload = () => {
        if (type === 'avatar') {
          setUserForm((prev) => ({ ...prev, avatar: finalUrl }));
          setAvatarPreview(finalUrl);
        } else {
          setUserForm((prev) => ({ ...prev, coverImage: finalUrl }));
          setCoverPreview(finalUrl);
        }
      };
      tester.onerror = (e: Event | string | undefined) => {
        console.warn('Image test failed to load', finalUrl, e);
        if (type === 'avatar') {
          setAvatarLastError('Preview failed to load');
          setUserForm((prev) => ({ ...prev, avatar: finalUrl }));
          setAvatarPreview(finalUrl);
        } else {
          setCoverLastError('Preview failed to load');
          setUserForm((prev) => ({ ...prev, coverImage: finalUrl }));
          setCoverPreview(finalUrl);
        }
      };
      tester.src = finalUrl;
      safeToast('success', 'Image uploaded');
    } catch (e: any) {
      console.error('Image upload error', e);
      const msg = e?.message || 'Image upload failed';
      safeToast('error', msg);
      if (type === 'avatar') setAvatarLastError(msg); else setCoverLastError(msg);
    } finally {
      if (type === 'avatar') {
        setAvatarUploadProgress(0);
        avatarCancelRef.current = () => {};
      } else {
        setCoverUploadProgress(0);
        coverCancelRef.current = () => {};
      }
    }
  };

  const cancelUpload = (type: 'avatar' | 'cover') => {
    if (type === 'avatar') {
      avatarCancelRef.current();
      setAvatarUploadProgress(0);
      setAvatarLastError('Upload cancelled');
    } else {
      coverCancelRef.current();
      setCoverUploadProgress(0);
      setCoverLastError('Upload cancelled');
    }
  };

  const retryUpload = (type: 'avatar' | 'cover') => {
    // Simpler UX: prompt user to pick the file again; we cannot re-read the File object programmatically
    safeToast('info', 'Please re-select the file to retry upload');
  };

  // After a hard refresh, invalidate stale blob: URLs persisted in localStorage
  React.useEffect(() => {
    let changed = false;
    if (avatarPreview && avatarPreview.startsWith('blob:')) {
      setAvatarPreview('');
      setUserForm((prev) => ({ ...prev, avatar: '' }));
      changed = true;
    }
    if (coverPreview && coverPreview.startsWith('blob:')) {
      setCoverPreview(DEFAULT_COVER_URL);
      setUserForm((prev) => ({ ...prev, coverImage: undefined as any }));
      changed = true;
    }
    if (changed) {
      // persist the cleanup so UI is consistent on next paint
      try {
        const payload = { userForm, avatarPreview: '', coverPreview: DEFAULT_COVER_URL } as any;
        localStorage.setItem(SIGNUP_PROGRESS_KEY, JSON.stringify(payload));
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Allow users to exit the signup flow cleanly (break any loop) and return to home
  const handleExitSignup = () => {
    // 1) Optimistic: clear local state and navigate immediately
    try {
      localStorage.removeItem(SIGNUP_PROGRESS_KEY);
      localStorage.removeItem('template_selection_user');
    } catch {}
    try { onBack(); } catch {}

    // 2) Background: best-effort username release and lead record with short timeout
    (async () => {
      const timeoutMs = 5000;
      const withTimeout = async (fn: (signal: AbortSignal) => Promise<any>) => {
        const ctl = new AbortController();
        const t = setTimeout(() => ctl.abort(), timeoutMs);
        try { await fn(ctl.signal); } catch {} finally { clearTimeout(t); }
      };

      // Release username if reserved
      await withTimeout(async (signal) => {
        try {
          const uname = (userForm.username || signupForm.username || '').trim().toLowerCase();
          if (uname) {
            const { getSignupSessionId } = await import('../utils/signupSession');
            const { releaseReservation } = await import('../services/api');
            const sid = getSignupSessionId();
            await releaseReservation(uname, sid);
          }
        } catch {}
      });

      // Record incomplete signup lead for admin follow-up
      await withTimeout(async (signal) => {
        try {
          const { recordIncompleteSignupLead } = await import('../api/leads');
          const { getSignupSessionId } = await import('../utils/signupSession');
          const fullName = (userForm.name || signupForm.name || '').trim();
          const email = String((userForm.email || signupForm.email || '')).trim();
          const phoneNumber = String((userForm.phone || '')).trim();
          const position = String((userForm.position || '')).trim();
          const country = String((userForm.location || '')).trim();
          const company = String((userForm.company || '')).trim();
          const reservedUsername = (userForm.username || signupForm.username || '').trim().toLowerCase();
          const sessionId = getSignupSessionId();
          if (fullName && email) {
            // recordIncompleteSignupLead already uses fetch; no direct signal, but we keep timebox overall
            await recordIncompleteSignupLead({ fullName, email, phoneNumber: phoneNumber || undefined, note: 'Exited during signup', position: position || undefined, country: country || undefined, company: company || undefined, sessionId, reservedUsername: reservedUsername || undefined });
          }
        } catch {}
      });

      // Also free email/id by abandoning any unfinished account
      await withTimeout(async () => {
        try {
          const { getSignupSessionId } = await import('../utils/signupSession');
          const sid = getSignupSessionId();
          const email = String((userForm.email || signupForm.email || '')).trim().toLowerCase();
          const username = (userForm.username || signupForm.username || '').trim().toLowerCase();
          if (email || username) {
            await fetch('/api/auth/abandon-incomplete', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: email || undefined, username: username || undefined, sessionId: sid }),
            });
          }
        } catch {}
      });
    })();
  };

  // ----- Handle/Username helpers (client mirror of server rules) -----
  const deriveHandle = React.useCallback((fullName: string) => {
    const words = (fullName || "").toLowerCase().replace(/[^a-z0-9 ]+/g, "").trim().split(/\s+/).filter(Boolean);
    if (!words.length) return "user";
    if (words.length === 1) return words[0].slice(0, 15);
    return (words[0] + words[1][0]).slice(0, 15);
  }, []);

  // Derived handle suggestion from name (after deriveHandle is defined)
  const handleSuggestion = React.useMemo(() => deriveHandle(userForm.name), [userForm.name, deriveHandle]);

  // Note: Only show server-verified suggestions (no local fallbacks)
  const SUGG_VER = 'v2';

  // Availability effect with debounce + abort + cache
  React.useEffect(() => {
  const handle = deriveHandle(userForm.name);
  const valueRaw = (debouncedUsername || "").trim().toLowerCase();
  const value = valueRaw || handle; // if user hasn't typed, check the handle candidate
  if (!value) { setUsernameOk(null); setUsernameSuggestions([]); return; }

    // cache hit
  const cached = usernameCache.get(`${SUGG_VER}:${handle}::${value}`);
    if (cached) {
      setUsernameOk(cached.ok);
      setUsernameSuggestions(cached.suggestions.map(v => ({ value: v })));
      return;
    }

    // abort previous
    if (usernameAbortRef.current) usernameAbortRef.current.abort();
    const ctl = new AbortController();
    usernameAbortRef.current = ctl;

  // Do not show speculative suggestions; wait for server-verified ones
  setUsernameSuggestions([]);

    setUsernameLoading(true);
  (async () => {
      try {
    let sid: string | undefined;
    try { sid = (await import('../utils/signupSession')).getSignupSessionId(); } catch {}
    const result = await checkUsername(value, sid);
        const ok: boolean = !!result.available;
        // Always try to fetch deterministic 3-pack (available-only); fall back to server suggestions
        let sugg: string[] = [];
        try {
          const pack = await suggestUsernames(value);
          const list = (pack?.candidates || [])
            .filter(c => c?.available)
            .map((c) => c.username)
            .filter(Boolean);
          if (list.length) sugg = list;
        } catch {}
        if (!sugg.length) {
          const base = Array.isArray(result.suggestions) ? result.suggestions : [];
          if (base.length) sugg = base;
        }
        // Ensure the available base candidate is visible first when ok
        if (ok) {
          const merged = [value, ...sugg];
          const dedup = Array.from(new Set(merged)).slice(0, 3);
          sugg = dedup;
        } else {
          // still cap at 3 for UI consistency
          sugg = sugg.slice(0, 3);
        }
  usernameCache.set(`${SUGG_VER}:${handle}::${value}`, { ok, suggestions: sugg });
        setUsernameOk(ok);
        setUsernameSuggestions(sugg.map(v => ({ value: v })));
        const primary = (sugg[0] || value) as string;
        setUsernameSuggested(primary);
      } catch (e: any) {
        if (e?.name === 'AbortError') return;
        setUsernameOk(true);
      } finally {
        setUsernameLoading(false);
      }
    })();

    return () => { ctl.abort(); };
  }, [debouncedUsername, userForm.name, deriveHandle, usernameCache]);

  // Allow autofill again if user clears the field
  React.useEffect(() => {
    if (!userForm.username) setUsernameAutofilled(false);
  }, [userForm.username]);

  // UsernameBlock removed (replaced by memoized UsernameField)

  // ----- Location Autocomplete -----
  const handleLocationInputChange = (value: string) => {
    setLocationInput(value);
    const city = String(cityInput || '').trim();
    const country = String(value || '').trim();
    const composed = city && country ? `${city}, ${country}` : (city || country);
    setUserForm((prev) => ({ ...prev, location: composed }));

    if (value.length > 0) {
      const filtered = countries.filter((country) =>
        country.toLowerCase().startsWith(value.toLowerCase())
      );
      setFilteredCountries(filtered.slice(0, 10)); // Show max 10 suggestions
      setShowLocationDropdown(filtered.length > 0);
    } else {
      setShowLocationDropdown(false);
      setFilteredCountries([]);
    }
  };

  const handleLocationSelect = (country: string) => {
    setLocationInput(country);
    const city = String(cityInput || '').trim();
    const composed = city ? `${city}, ${country}` : country;
    setUserForm((prev) => ({ ...prev, location: composed }));
    setShowLocationDropdown(false);
    setFilteredCountries([]);
  };

  const handleCityInputChange = (value: string) => {
    setCityInput(value);
    const city = String(value || '').trim();
    const country = String(locationInput || '').trim();
    const composed = city && country ? `${city}, ${country}` : (city || country);
    setUserForm((prev) => ({ ...prev, location: composed }));

    if (value.length > 0) {
      const filtered = cities.filter((c) => c.toLowerCase().startsWith(value.toLowerCase()));
      setFilteredCities(filtered.slice(0, 10));
      setShowCityDropdown(filtered.length > 0);
    } else {
      setShowCityDropdown(false);
      setFilteredCities([]);
    }
  };

  // On explicit city selection from dropdown: auto-fill country if known
  const handleCitySelect = (city: string) => {
    const normalized = city.trim().toLowerCase();
    const expected = cityCountryMap[normalized];
    setCityInput(city);
    let newCountry = locationInput;
    if (expected && (!locationInput || locationInput.toLowerCase() !== expected.toLowerCase())) {
      newCountry = expected;
      setLocationInput(expected);
    }
    const composed = city && newCountry ? `${city}, ${newCountry}` : (city || newCountry);
    setUserForm((prev) => ({ ...prev, location: composed }));
    setShowCityDropdown(false);
    setFilteredCities([]);
  };

  // ----- User Signup -----
  const handleUserSignup = (e: React.FormEvent) => {
    e.preventDefault();

    const requireProfile = !!localAuthSettings?.userAuth?.signUp?.requireProfilePicture;
    if (requireProfile && !userForm.avatar) {
      safeToast('warning', 'Profile picture is required. Please upload your profile picture.');
      return;
    }

    if (!userForm.password || userForm.password.length < 6) {
      safeToast('warning', 'Password must be at least 6 characters long.');
      return;
    }

    if (crmFlow && !(userForm.crmCategory || '').trim()) {
      safeToast('warning', t('auth_field_crm_category_required'));
      return;
    }

    // Username mandatory
    if (!(userForm.username || '').trim()) {
      safeToast('warning', 'Please choose a username for your card address.');
      return;
    }

    // Block submit if city-country is a known mismatched pair
    if (cityCountryMismatch) {
      safeToast('error', `City and country don't match. ${cityInput} is in ${expectedCountry}.`);
      return;
    }

  setIsSubmitting(true);

    // Helper: persist and navigate to template selection
    const proceedToTemplate = (profile: any) => {
      try { localStorage.setItem('template_selection_user', JSON.stringify(profile)); } catch {}
      onUserSignup?.(profile);
    };

    // Build initial profile
    const handle = deriveHandle(userForm.name);
    let uname = (userForm.username || handle).toLowerCase();
    let localProfile: any = {
      handle,
      username: uname,
      name: userForm.name || signupForm.name || '',
      email: userForm.email || signupForm.email || '',
      password: userForm.password || signupForm.password || '',
      phone: userForm.phone || '',
      position: userForm.position || '',
      company: userForm.company || '',
      crmCategory: userForm.crmCategory || '',
      location: userForm.location || '',
      bio: userForm.bio || '',
      avatar: userForm.avatar,
      avatarUrl: userForm.avatar,
      coverImage: userForm.coverImage || defaultCoverImage,
      coverUrl: userForm.coverImage || defaultCoverImage,
    };

    // 1) Immediately proceed to next step with local profile (optimistic UI)
    proceedToTemplate(localProfile);
    setIsSubmitting(false);

    // 2) Fire-and-forget atomic server signup; update localStorage when done
    (async () => {
      try {
        // Use session-aware signup to respect any same-session reservations and ensure cleanup
        let sid: string | undefined;
        try {
          const m = await import('../utils/signupSession');
          sid = m.getSignupSessionId();
        } catch {}
        const input = {
          name: localProfile.name,
          email: localProfile.email,
          password: localProfile.password,
          phone: localProfile.phone,
          company: localProfile.company,
          position: localProfile.position,
          location: localProfile.location,
          gender: (userForm as any).gender || undefined,
          age: (userForm as any).age ? Number((userForm as any).age) : undefined,
          avatarUrl: localProfile.avatarUrl || localProfile.avatar || null,
          coverUrl: localProfile.coverUrl || localProfile.coverImage || null,
          templateKey: localProfile.templateKey,
          preferredHandle: handle,
          desiredUsername: uname,
          sessionId: sid,
        } as any;
        try {
          const out = await signupAtomic(input);
          const merged = { ...localProfile, ...out.user, cardId: out.card?.id };
          // Persist token and lightweight session so owner APIs work immediately and across refresh
          try {
            if (out?.token) localStorage.setItem('owner_token', out.token);
            const sess = { id: out?.user?.id || out?.user?._id || null, username: out?.user?.username || null };
            localStorage.setItem('owner_session', JSON.stringify(sess));
          } catch {}
          try { localStorage.setItem('template_selection_user', JSON.stringify(merged)); } catch {}
        } catch (e: any) {
          // Parse error to decide the right UX; only show username-specific toast for username conflicts
          let errCode: string | undefined;
          try {
            const parsed = JSON.parse(String(e?.message || ''));
            errCode = parsed?.error;
          } catch {
            const msg = String(e?.message || '').toUpperCase();
            if (msg.includes('USERNAME_TAKEN')) errCode = 'USERNAME_TAKEN';
            else if (msg.includes('USERNAME_RESERVED')) errCode = 'USERNAME_RESERVED';
            else if (msg.includes('EMAIL_TAKEN')) errCode = 'EMAIL_TAKEN';
          }

          if (errCode === 'EMAIL_TAKEN') {
            safeToast('error', 'Email is already registered. Try signing in or use a different email.');
            return;
          }

          if (errCode === 'USERNAME_TAKEN' || errCode === 'USERNAME_RESERVED') {
            try {
              const chk = await checkUsername(uname, sid);
              let sugg: string[] = (chk && chk.suggestions) || [];
              if (!chk?.available || !sugg.length) {
                try {
                  const pack = await suggestUsernames(uname);
                  const list = (pack?.candidates || []).filter(c => c?.available).map((c) => c.username).filter(Boolean);
                  if (list.length) sugg = list;
                } catch {}
              }
              if (!chk?.available && sugg.length) {
                setUsernameOk(false);
                setUsernameSuggestions(sugg.map((v: string) => ({ value: v })));
                safeToast('warning', 'That username was just taken. Please pick one of the suggestions.');
                return;
              }
            } catch {}
          }

          // Fallback generic error for non-username failures (e.g., captcha/network)
          safeToast('error', 'Signup failed. Please try again.');
        }
      } catch (err) {
        try { console.warn('[signup] atomic create failed', (err as any)?.message || err); } catch {}
      }
    })();
  };

  // ----- User Signin -----
  const handleUserSignin = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate CAPTCHA for user signin (FIXED to use state)
    if (userCaptchaInput.trim().toUpperCase() !== userGeneratedCaptcha.trim().toUpperCase()) {
    safeToast('error', 'Invalid CAPTCHA. Please try again.');
      generateUserCaptcha();
      return;
    }

    onUserSignin(userSigninForm);
  };

  // ----- Admin Submit -----
  const handleAdminSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate CAPTCHA when enabled
    if (adminCaptchaEnabled && captchaCode.trim().toUpperCase() !== generatedCaptcha.trim().toUpperCase()) {
    safeToast('error', 'Invalid CAPTCHA. Please try again.');
      generateAdminCaptcha();
      return;
    }

    // 2FA enforced server-side when enabled in site settings — never call login without proof
    if (adminTwoFaEnabled && (isLogin || isAdminCreated)) {
      setIsVerifyingTwoFA(true);
      const sent = await sendTwoFACode(adminForm.email);
      setIsVerifyingTwoFA(false);
      if (sent) {
        setShowTwoFA(true);
      }
      return;
    } else if (isLogin || isAdminCreated) {
      onAdminLogin(adminForm);
    } else {
      // First admin signup - no 2FA needed in this flow
      onAdminLogin(adminForm);
    }
  };

  const handleTwoFAVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = twoFACode.trim();
    if (code.length !== 6) return;
    try {
      const r = await verifyEmailOtp(adminForm.email, code, 'admin_login');
      if (r.ok && r.proof) {
        onAdminLogin(adminForm, r.proof);
        setShowTwoFA(false);
        setIsVerifyingTwoFA(false);
        setTwoFACode('');
        return;
      }
      if (r.ok && !r.proof) {
        safeToast('error', 'Verification succeeded but login proof was missing. Please try again.');
        setTwoFACode('');
        return;
      }
      safeToast('error', 'Invalid verification code. Please check your email and try again.');
      setTwoFACode('');
    } catch {
      safeToast('error', 'Verification failed. Please try again.');
      setTwoFACode('');
    }
  };

  const generateUsername = (fullName: string): string => {
    const parts = fullName
      .toLowerCase()
      .split(' ')
      .filter((p) => p.length > 0);

    if (parts.length === 1) return parts[0].substring(0, 8);
    if (parts.length === 2) return parts[0] + parts[1].charAt(0);
    if (parts.length >= 3) return parts[0] + parts[1].charAt(0) + parts[2].charAt(0);
    return 'user';
  };

  return (
    <div className={embedded ? 'w-full' : 'min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4 sm:p-6'}>
      <div className={embedded ? 'w-full' : 'w-full max-w-sm sm:max-w-md lg:max-w-lg'}>
        {/* Back Button */}
        {!hideExit && (
        <button
          type="button"
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4 sm:mb-6 transition-colors text-sm sm:text-base"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {backLabel || 'Back to Home'}
        </button>
        )}

        <div className={`bg-white rounded-2xl overflow-hidden ${embedded ? 'shadow-none' : 'shadow-2xl'}`}>
          {/* Header */}
          <div
            className={`${compact ? 'p-5' : 'p-8'} text-white ${
              authType === 'admin'
                ? 'bg-gradient-to-r from-red-600 to-red-700'
                : 'bg-gradient-to-r from-blue-600 to-indigo-600'
            }`}
          >
            <div className="text-center">
              <div
                className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                  authType === 'admin' ? 'bg-white/20' : 'bg-white/20'
                }`}
              >
                {authType === 'admin' ? (
                  <Shield className="w-8 h-8 text-white" />
                ) : (
                  <User className="w-8 h-8 text-white" />
                )}
              </div>
              <h2 className="text-xl sm:text-2xl font-bold mb-2">
                {authType === 'admin' ? t('auth_admin_header_title', 'Admin Access') : t('auth_user_header_title')}
              </h2>
              <p
                className={`text-sm sm:text-base ${
                  authType === 'admin' ? 'text-red-100' : 'text-blue-100'
                }`}
              >
                {authType === 'admin'
                  ? t('auth_admin_header_subtitle', 'System administrator login')
                  : isLogin
                  ? t('auth_user_header_signin')
                  : t('auth_user_header_signup')}
              </p>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-6 sm:p-8">
            {authType === 'admin' ? (
              <>
                {/* Admin Login/Signup Toggle */}
                {!isAdminCreated && (
                  <div className="flex bg-gray-100 rounded-xl p-1 mb-4 sm:mb-6">
                    <button
                      type="button"
                      onClick={() => setIsLogin(true)}
                      className={`flex-1 py-2 px-3 sm:px-4 rounded-lg font-medium transition-all text-sm sm:text-base ${
                        isLogin ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Login
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsLogin(false)}
                      className={`flex-1 py-2 px-3 sm:px-4 rounded-lg font-medium transition-all text-sm sm:text-base ${
                        !isLogin ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Create Admin
                    </button>
                  </div>
                )}

                {isAdminCreated && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                    <div className="flex items-center gap-2 text-red-800">
                      <Shield className="w-5 h-5" />
                      <p className="font-semibold">Secure Admin Access</p>
                    </div>
                    <p className="text-sm text-red-700 mt-1">
                      Admin account already exists. Please sign in with your credentials.
                    </p>
                  </div>
                )}

                {/* Two-Factor Authentication Modal */}
                {showTwoFA && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 relative">
                      <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Smartphone className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Two-Factor Authentication</h3>
                        <p className="text-gray-600">Enter the 6-digit code sent to your email</p>
                      </div>

                      <form onSubmit={handleTwoFAVerification} className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
                            Verification Code
                          </label>
                          <input
                            type="text"
                            value={twoFACode}
                            onChange={(e) =>
                              setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))
                            }
                            required
                            maxLength={6}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 text-center text-2xl font-mono tracking-widest"
                            placeholder="000000"
                          />
                          <p className="text-xs text-gray-500 text-center mt-2">
                            Check your email for the 6-digit verification code
                          </p>
                        </div>

                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setShowTwoFA(false);
                              setIsVerifyingTwoFA(false);
                              setTwoFACode('');
                            }}
                            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={twoFACode.length !== 6}
                            className="flex-1 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Verify & Login
                          </button>
                        </div>
                      </form>

                      <div className="mt-4 text-center">
                        <button
                          type="button"
                          onClick={() => sendTwoFACode(adminForm.email)}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Resend Code
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Admin Form */}
                {!showTwoFA && (
                  <form onSubmit={handleAdminSubmit} className="space-y-4 sm:space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Mail className="w-4 h-4 inline mr-1" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={adminForm.email}
                        onChange={(e) =>
                          setAdminForm((prev) => ({ ...prev, email: e.target.value }))
                        }
                        required
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                        placeholder="admin@tcards.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Lock className="w-4 h-4 inline mr-1" />
                        Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={adminForm.password}
                          onChange={(e) =>
                            setAdminForm((prev) => ({ ...prev, password: e.target.value }))
                          }
                          required
                          className="w-full px-3 sm:px-4 py-2 sm:py-3 pr-10 sm:pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    {/* CAPTCHA Security */}
                    {adminCaptchaEnabled && (
                    <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                      <label className="block text-sm font-medium text-red-800 mb-3">
                        <Shield className="w-4 h-4 inline mr-1" />
                        Security Verification (CAPTCHA)
                      </label>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="bg-white border-2 border-red-300 rounded-lg p-3 font-mono text-xl font-bold text-gray-800 tracking-widest select-none bg-gradient-to-r from-gray-100 to-gray-200">
                          {generatedCaptcha}
                        </div>
                        <button
                          type="button"
                          onClick={generateAdminCaptcha}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          title="Generate new CAPTCHA"
                        >
                          <RefreshCw className="w-5 h-5" />
                        </button>
                      </div>
                      <input
                        type="text"
                        value={captchaCode}
                        onChange={(e) => setCaptchaCode(e.target.value.toUpperCase())}
                        required
                        maxLength={6}
                        className="w-full px-4 py-3 border border-red-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 text-center font-mono text-lg tracking-widest"
                        placeholder="Enter CAPTCHA"
                      />
                      <p className="text-xs text-red-700 mt-2">Enter the 6-character code shown above</p>
                    </div>
                    )}

                    <button
                      type="submit"
                      disabled={isVerifyingTwoFA}
                      className="w-full py-3 sm:py-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-lg hover:shadow-xl font-semibold text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isVerifyingTwoFA ? (
                        <div className="flex items-center justify-center">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Sending Verification Code...
                        </div>
                      ) : (isLogin || isAdminCreated) ? (adminTwoFaEnabled ? 'Secure Login' : 'Login') : 'Create Admin Account'}
                    </button>

                    {adminTwoFaEnabled && (isLogin || isAdminCreated) && (
                      <div className="bg-yellow-50 p-4 rounded-xl border border-yellow-200">
                        <div className="flex items-center gap-2 text-yellow-800 mb-2">
                          <Smartphone className="w-4 h-4" />
                          <p className="font-semibold text-sm">Two-Factor Authentication</p>
                        </div>
                        <p className="text-xs text-yellow-700">
                          After entering your credentials, you'll receive a verification code via email for enhanced security.
                        </p>
                      </div>
                    )}
                  </form>
                )}
              </>
            ) : (
              <>
                {/* User Login/Signup Toggle */}
                <div className="flex bg-gradient-to-r from-blue-100 to-indigo-100 rounded-xl p-1 mb-6">
                  <button
                    type="button"
                    onClick={() => setIsLogin(true)}
                    className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all text-sm sm:text-base ${
                      isLogin
                        ? 'bg-white text-blue-600 shadow-lg transform scale-105'
                        : 'text-blue-600 hover:text-blue-800'
                    }`}
                  >
                    <User className="w-4 h-4 inline mr-2" />
                    Sign In
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsLogin(false)}
                    className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all text-sm sm:text-base ${
                      !isLogin
                        ? 'bg-white text-blue-600 shadow-lg transform scale-105'
                        : 'text-blue-600 hover:text-blue-800'
                    }`}
                  >
                    <Sparkles className="w-4 h-4 inline mr-2" />
                    Create Card
                  </button>
                </div>

                {isLogin ? (
                  /* User Sign In Form */
                  <form onSubmit={handleUserSignin} className="space-y-6">
                    <div className="text-center mb-6">
                      <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <User className="w-10 h-10 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Welcome Back!</h3>
                      <p className="text-gray-600">
                        {typeof window !== 'undefined' && localStorage.getItem('auth_intent') === 'owner_ads'
                          ? 'Sign in to access Ads Panel'
                          : 'Sign in to access your digital business card'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Mail className="w-4 h-4 inline mr-1" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={userSigninForm.email}
                        onChange={(e) =>
                          setUserSigninForm((prev) => ({ ...prev, email: e.target.value }))
                        }
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                        placeholder="Enter your email address"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Lock className="w-4 h-4 inline mr-1" />
                        Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={userSigninForm.password}
                          onChange={(e) =>
                            setUserSigninForm((prev) => ({ ...prev, password: e.target.value }))
                          }
                          required
                          className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    {/* CAPTCHA Security for User Sign In */}
                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                      <label className="block text-sm font-medium text-blue-800 mb-3">
                        <Shield className="w-4 h-4 inline mr-1" />
                        Security Verification (CAPTCHA)
                      </label>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="bg-white border-2 border-blue-300 rounded-lg p-3 font-mono text-lg font-bold text-gray-800 tracking-widest select-none bg-gradient-to-r from-blue-100 to-indigo-100">
                          {userGeneratedCaptcha}
                        </div>
                        <button
                          type="button"
                          onClick={generateUserCaptcha}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                          title="Generate new CAPTCHA"
                        >
                          <RefreshCw className="w-5 h-5" />
                        </button>
                      </div>
                      <input
                        type="text"
                        value={userCaptchaInput}
                        onChange={(e) => setUserCaptchaInput(e.target.value.toUpperCase())}
                        required
                        maxLength={5}
                        className="w-full px-4 py-3 border border-blue-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-center font-mono text-lg tracking-widest"
                        placeholder="Enter CAPTCHA"
                      />
                      <p className="text-xs text-blue-700 mt-2">Enter the 5-character code shown above</p>
                    </div>

                    <button
                      type="submit"
                      className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl font-semibold text-base -translate-y-0"
                    >
                      Sign In to Your Card
                    </button>

                    <div className="text-center">
                      <p className="text-sm text-gray-600">
                        Forgot your password?{' '}
                        <button
                          type="button"
                          onClick={async () => {
                            const email = (userSigninForm.email || '').trim();
                            if (!email) return safeToast('error', 'Enter your email first');
                            try {
                              const res = await fetch('/api/users/password-reset/request', {
                                method: 'POST',
                                headers: { 'content-type': 'application/json' },
                                body: JSON.stringify({ email })
                              });
                              if (res.ok) {
                                safeToast('success', 'If that email exists, a reset link has been sent');
                              } else {
                                const err = await res.json().catch(() => ({}));
                                safeToast('error', err?.error || 'Could not request reset');
                              }
                            } catch (e) {
                              console.error(e);
                              safeToast('error', 'Network error');
                            }
                          }}
                          className="text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Reset it here
                        </button>
                      </p>
                    </div>
                  </form>
                ) : (
                  /* User Signup Form */
                  <form onSubmit={handleUserSignup} className="space-y-6" autoComplete="off">
                    {/* Engaging Header */}
                    <div className="text-center mb-6">
                      <div className="flex items-center justify-center gap-2 mb-4">
                        <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                        <div className="w-3 h-3 bg-indigo-500 rounded-full animate-pulse delay-100"></div>
                        <div className="w-3 h-3 bg-purple-500 rounded-full animate-pulse delay-200"></div>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{crmFlow ? t('auth_create_your_account') : t('auth_signup_title')}</h3>
                      <p className="text-gray-600">{crmFlow ? t('auth_crm_signup_subtitle') : t('auth_signup_subtitle')}</p>
                    </div>

                    {/* Profile Picture Upload - Required */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-6 rounded-xl border border-blue-200">
                      <h4 className="text-base font-bold text-blue-900 mb-4 flex items-center">
                        <Image className="w-5 h-5 mr-2" />
                        {t('auth_profile_picture')} *
                        <Star className="w-4 h-4 ml-2 text-yellow-500" />
                      </h4>
                      <div className="space-y-4">
                        <div className="relative">
                          {avatarPreview ? (
                            <div className="relative">
                              <img
                                src={avatarPreview}
                                alt="Profile preview"
                                onError={(e) => {
                                  console.warn('Avatar preview failed to load', avatarPreview);
                                  setAvatarLastError('Preview failed to load');
                                }}
                                className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg mx-auto"
                              />
                              <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                                <CheckCircle className="w-4 h-4 text-white" />
                              </div>
                            </div>
                          ) : (
                            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-gray-200 to-gray-300 border-4 border-white shadow-lg flex items-center justify-center mx-auto">
                              <User className="w-8 h-8 text-gray-500" />
                            </div>
                          )}
                          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
                              <Image className="w-3 h-3 text-white" />
                            </div>
                        </div>
                        <div>
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/svg+xml"
                            onClick={(e) => { (e.currentTarget as HTMLInputElement).value = ''; }}
                            onChange={(e) => { const f = e.target.files?.[0] || null; handleImageUpload('avatar', f); /* keep value so filename is visible */ }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-all duration-200"
                          />
                          {avatarUploadProgress > 0 && (
                            <div className="mt-2">
                              <ProgressBar percent={avatarUploadProgress} />
                              <div className="flex items-center gap-2 mt-2">
                                <button type="button" onClick={() => cancelUpload('avatar')} className="text-sm text-red-600">{t('auth_upload_cancel')}</button>
                                <button type="button" onClick={() => retryUpload('avatar')} className="text-sm text-gray-600">{t('auth_upload_retry')}</button>
                                <div className="text-sm text-blue-700 ml-auto">{avatarUploadProgress}%</div>
                              </div>
                            </div>
                          )}
                          {avatarLastError && (
                            <div className="mt-2 text-sm text-red-600">{avatarLastError}</div>
                          )}
                          <p className="text-xs text-gray-500 mt-2">{t('auth_upload_large_hint', { mb: (MAX_FRONTEND_UPLOAD_BYTES/1024/1024).toFixed(0) })}</p>
                        </div>
                        <div className="text-center space-y-2">
                          <p className="text-sm text-blue-700 font-medium">{t('auth_profile_required')}</p>
                          <p className="text-xs text-gray-500">{t('auth_profile_formats')}</p>
                        </div>
                      </div>
                    </div>

                    {/* Cover Image Upload - Optional (configurable) */}
                    {localAuthSettings?.userAuth?.signUp?.enableCoverUpload !== false && (
                    <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-6 rounded-xl border border-green-200">
                      <h4 className="text-base font-bold text-green-900 mb-4 flex items-center">
                        <Image className="w-5 h-5 mr-2" />
                        {t('auth_cover_image')}
                        <Sparkles className="w-4 h-4 ml-2 text-green-600" />
                      </h4>
                      <div className="space-y-4">
                        <div className="relative">
                          <img
                            src={coverPreview || DEFAULT_COVER_URL}
                            alt="Cover preview"
                            onError={(e) => {
                              const img = e.currentTarget as HTMLImageElement;
                              // If default fails, fall back to remote stock photo; otherwise ensure we at least show default
                              if (!img.src.endsWith(REMOTE_FALLBACK_COVER_URL)) {
                                img.src = REMOTE_FALLBACK_COVER_URL;
                              }
                            }}
                            className="w-full h-32 rounded-xl object-cover border-2 border-white shadow-lg"
                          />
                          <div className="absolute top-2 right-2 px-3 py-1 bg-black/50 text-white text-xs rounded-full">
                            {coverPreview && ![DEFAULT_COVER_URL, REMOTE_FALLBACK_COVER_URL].includes(coverPreview) ? t('auth_cover_custom') : t('auth_cover_default')}
                          </div>
                        </div>
                        <div>
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/svg+xml"
                            onClick={(e) => { (e.currentTarget as HTMLInputElement).value = ''; }}
                            onChange={(e) => { const f = e.target.files?.[0] || null; handleImageUpload('cover', f); /* keep value so filename is visible */ }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100 transition-all duration-200"
                          />
                          {coverUploadProgress > 0 && (
                            <div className="mt-2">
                              <ProgressBar percent={coverUploadProgress} />
                              <div className="flex items-center gap-2 mt-2">
                                <button type="button" onClick={() => cancelUpload('cover')} className="text-sm text-red-600">{t('auth_upload_cancel')}</button>
                                <button type="button" onClick={() => retryUpload('cover')} className="text-sm text-gray-600">{t('auth_upload_retry')}</button>
                                <div className="text-sm text-green-700 ml-auto">{coverUploadProgress}%</div>
                              </div>
                            </div>
                          )}
                          {coverLastError && (
                            <div className="mt-2 text-sm text-red-600">{coverLastError}</div>
                          )}
                          <div className="mt-3 space-y-2 text-center">
                            <p className="text-sm text-green-700 font-medium">
                              {t('auth_cover_hint')}
                            </p>
                            <p className="text-xs text-gray-500">{t('auth_upload_large_hint', { mb: (MAX_FRONTEND_UPLOAD_BYTES/1024/1024).toFixed(0) })}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    )}

                    {/* Personal Information */}
                    <div className="bg-gradient-to-br from-purple-50 to-violet-100 p-6 rounded-xl border border-purple-200">
                      <h4 className="text-base font-bold text-purple-900 mb-4 flex items-center">
                        <User className="w-5 h-5 mr-2" />
                        {t('auth_personal_info')}
                        <CheckCircle className="w-4 h-4 ml-2 text-purple-600" />
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <User className="w-4 h-4 inline mr-1" />
                            {t('auth_field_name_label')} *
                          </label>
                          <input
                            type="text"
                            name="signup-fullname"
                            autoComplete="name"
                            value={userForm.name}
                            onChange={(e) => setUserForm((prev) => ({ ...prev, name: e.target.value }))}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                            placeholder={t('auth_field_name_placeholder')}
                          />
                          {userForm.name && (
                            <p className="text-xs text-blue-600 mt-1 font-medium">
                              ✨ {t('auth_url_preview', { url: `www.${handleSuggestion}.tcards.org${userForm.username ? `/${userForm.username}` : ''}` })}
                            </p>
                          )}
                        </div>

                        {/* Username selector (debounced availability), configurable */}
                        {/* Username selector: always compulsory; hide label; use customizable placeholder */}
                        <div className="sm:col-span-2">
                          <UsernameField
                            value={userForm.username || ""}
                            onChange={(u) => { setIsTypingUsername(true); setUserForm((prev) => ({ ...prev, username: u.toLowerCase() })); }}
                            onBlur={() => setIsTypingUsername(false)}
                            loading={usernameLoading}
                            ok={usernameOk}
                            suggestions={usernameSuggestions}
                            placeholder={
                              localAuthSettings?.userAuth?.signUp?.usernameField?.placeholder
                                || (!userForm.username && usernameOk ? `e.g. ${handleSuggestion}` : t('auth_field_username_enter'))
                            }
                          />
                        </div>
                        {/* Removed duplicate Card Address block (redundant UsernameBlock) */}

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Mail className="w-4 h-4 inline mr-1" />
                            {t('auth_field_email_label')} *
                          </label>
                          <input
                            type="email"
                            name="signup-email"
                            autoComplete="email"
                            value={userForm.email}
                            onChange={(e) => setUserForm((prev) => ({ ...prev, email: e.target.value }))}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                            placeholder={t('auth_field_email_placeholder')}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Lock className="w-4 h-4 inline mr-1" />
                            {t('auth_field_password_label')} *
                          </label>
                          <div className="relative">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              name="signup-password"
                              autoComplete="new-password"
                              value={userForm.password}
                              onChange={(e) =>
                                setUserForm((prev) => ({ ...prev, password: e.target.value }))
                              }
                              required
                              minLength={6}
                              className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                              placeholder={t('auth_field_password_placeholder')}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{t('auth_password_min')}</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Phone className="w-4 h-4 inline mr-1" />
                            {t('auth_field_phone_label')} *
                          </label>
                          <input
                            type="tel"
                            name="signup-phone"
                            autoComplete="tel"
                            value={userForm.phone}
                            onChange={(e) => setUserForm((prev) => ({ ...prev, phone: e.target.value }))}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                            placeholder={t('auth_field_phone_placeholder')}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth_field_position_label')} *</label>
                          <input
                            type="text"
                            value={userForm.position}
                            onChange={(e) =>
                              setUserForm((prev) => ({ ...prev, position: e.target.value }))
                            }
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                            placeholder={t('auth_field_position_placeholder')}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Building className="w-4 h-4 inline mr-1" />
                            {t('auth_field_company_label')} *
                          </label>
                          <input
                            type="text"
                            value={userForm.company}
                            onChange={(e) =>
                              setUserForm((prev) => ({ ...prev, company: e.target.value }))
                            }
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                            placeholder={t('auth_field_company_placeholder')}
                          />
                        </div>

                        {crmFlow && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {t('auth_field_crm_category_label')} *
                            </label>
                            <select
                              value={userForm.crmCategory || ''}
                              onChange={(e) =>
                                setUserForm((prev) => ({ ...prev, crmCategory: e.target.value }))
                              }
                              required
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                            >
                              <option value="">{t('auth_field_crm_category_placeholder')}</option>
                              <option value="sales">{t('crmCategory_sales')}</option>
                              <option value="general">{t('crmCategory_general')}</option>
                              <option value="cleaning">{t('crmCategory_cleaning')}</option>
                              <option value="security">{t('crmCategory_security')}</option>
                              <option value="technical">{t('crmCategory_technical')}</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-1">{t('auth_field_crm_category_hint')}</p>
                          </div>
                        )}

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth_field_gender_label')} *</label>
                          <select
                            value={userForm.gender}
                            onChange={(e) => setUserForm((prev) => ({ ...prev, gender: e.target.value }))}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                          >
                            <option value="">{t('auth_field_gender_placeholder')}</option>
                            <option value="male">{t('auth_gender_male')}</option>
                            <option value="female">{t('auth_gender_female')}</option>
                            <option value="other">{t('auth_gender_other')}</option>
                            <option value="prefer_not_to_say">{t('auth_gender_prefer_not')}</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Calendar className="w-4 h-4 inline mr-1" />
                            {t('auth_field_age_label')} *
                          </label>
                          <select
                            value={userForm.age}
                            onChange={(e) => setUserForm((prev) => ({ ...prev, age: e.target.value }))}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                          >
                            <option value="">{t('auth_field_age_placeholder')}</option>
                            <option value="18-25">18-25</option>
                            <option value="25-35">25-35</option>
                            <option value="35-45">35-45</option>
                            <option value="45-55">45-55</option>
                            <option value="55+">55+</option>
                          </select>
                        </div>

                        <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <MapPin className="w-4 h-4 inline mr-1" />
                              {t('auth_field_city_label')}
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                value={cityInput}
                                onChange={(e) => handleCityInputChange(e.target.value)}
                                onFocus={() => {
                                  if (cityInput.length > 0) {
                                    const filtered = cities.filter((c) =>
                                      c.toLowerCase().startsWith(cityInput.toLowerCase())
                                    );
                                    setFilteredCities(filtered.slice(0, 10));
                                    setShowCityDropdown(filtered.length > 0);
                                  }
                                }}
                                onBlur={() => {
                                  setTimeout(() => setShowCityDropdown(false), 200);
                                }}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                                placeholder={t('auth_field_city_placeholder')}
                                autoComplete="off"
                              />

                              {/* City Dropdown */}
                              {showCityDropdown && filteredCities.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                  {filteredCities.map((city, index) => (
                                    <button
                                      key={index}
                                      type="button"
                                      onClick={() => handleCitySelect(city)}
                                      className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 text-sm"
                                    >
                                      <div className="flex items-center">
                                        <MapPin className="w-4 h-4 mr-2 text-blue-500" />
                                        {city}
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              )}
                              {cityCountryMismatch && (
                                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                  <p className="text-xs text-red-700">
                                    {t('auth_city_mismatch', { city: cityInput, country: expectedCountry })}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <MapPin className="w-4 h-4 inline mr-1" />
                              {t('auth_field_country_label')} *
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                value={locationInput}
                                onChange={(e) => handleLocationInputChange(e.target.value)}
                                onFocus={() => {
                                  if (locationInput.length > 0) {
                                    const filtered = countries.filter((country) =>
                                      country.toLowerCase().startsWith(locationInput.toLowerCase())
                                    );
                                    setFilteredCountries(filtered.slice(0, 10));
                                    setShowLocationDropdown(filtered.length > 0);
                                  }
                                }}
                                onBlur={() => {
                                  // Delay hiding dropdown to allow selection
                                  setTimeout(() => setShowLocationDropdown(false), 200);
                                }}
                                required
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                                placeholder={t('auth_field_location_placeholder')}
                                autoComplete="off"
                              />

                              {/* Country Dropdown */}
                              {showLocationDropdown && filteredCountries.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                  {filteredCountries.map((country, index) => (
                                    <button
                                      key={index}
                                      type="button"
                                      onClick={() => handleLocationSelect(country)}
                                      className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 text-sm"
                                    >
                                      <div className="flex items-center">
                                        <MapPin className="w-4 h-4 mr-2 text-blue-500" />
                                        {country}
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              )}
                              {cityCountryMismatch && (
                                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                  <p className="text-xs text-red-700">
                                    {t('auth_city_mismatch_pick', { city: cityInput, country: expectedCountry })}
                                  </p>
                                </div>
                              )}
                            </div>
                            {/* Custom city notice */}
                            {cityInput.length > 0 &&
                              !cities.some((c) => c.toLowerCase() === cityInput.toLowerCase()) && (
                                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                  <p className="text-xs text-yellow-800">
                                    <span className="font-medium">{t('auth_custom_city')}</span> "{cityInput}". {t('auth_custom_city_note')}
                                  </p>
                                </div>
                              )}
                            {/* Safe escape hatch for custom country */}
                            {locationInput.length > 0 &&
                              !countries.some(
                                (country) => country.toLowerCase() === locationInput.toLowerCase()
                              ) && (
                                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                  <p className="text-xs text-yellow-800 mb-2">
                                    <span className="font-medium">{t('auth_custom_country')}</span> "{locationInput}"
                                  </p>
                                  <p className="text-xs text-yellow-700">
                                    {t('auth_custom_country_note')}
                                  </p>
                                </div>
                              )}
                          </div>
                        </div>

                          <div className="sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('auth_field_bio_label')}</label>
                            <textarea
                              value={userForm.bio}
                              onChange={(e) => setUserForm((prev) => ({ ...prev, bio: e.target.value }))}
                              rows={4}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base resize-none"
                              placeholder={t('auth_field_bio_placeholder')}
                            />
                            <p className="text-xs text-gray-500 mt-1">{t('auth_bio_public_hint')}</p>
                          </div>
                      </div>
                    </div>

                    {/* What's Next Section */}
                    <div className="bg-gradient-to-br from-yellow-50 to-amber-100 p-6 rounded-xl border border-yellow-200">
                      <h4 className="text-base font-bold text-yellow-900 mb-4 flex items-center">
                        <Sparkles className="w-5 h-5 mr-2" />
                        {t('auth_whats_next')}
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {(crmFlow
                          ? [
                              t('auth_crm_next_step_1'),
                              t('auth_crm_next_step_2'),
                              t('auth_crm_next_step_3'),
                              t('auth_crm_next_step_4'),
                            ]
                          : [
                              t('auth_next_step_1'),
                              t('auth_next_step_2'),
                              t('auth_next_step_3'),
                              t('auth_next_step_4', {
                                url: `www.${userForm.name ? generateUsername(userForm.name) : 'yourname'}.tcards.org`,
                              }),
                            ]
                        ).map((label, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <div className={`w-8 h-8 bg-gradient-to-r ${
                              idx === 0 ? 'from-blue-500 to-blue-600'
                                : idx === 1 ? 'from-green-500 to-green-600'
                                  : idx === 2 ? 'from-purple-500 to-purple-600'
                                    : 'from-orange-500 to-orange-600'
                            } rounded-full flex items-center justify-center text-white font-bold text-sm`}>
                              {idx + 1}
                            </div>
                            <p className="text-sm text-gray-700 break-all">{label}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full py-4 rounded-xl transition-all duration-200 shadow-lg font-bold text-base relative overflow-hidden ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl'}`}
                    >
                      <span className="relative flex items-center justify-center">
                        {isSubmitting ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            {crmFlow ? t('auth_creating_account') : t('auth_signup_creating')}
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-5 h-5 mr-2" />
                            {crmFlow ? t('auth_create_your_account') : t('auth_signup_button_trial')}
                          </>
                        )}
                      </span>
                    </button>
                  </form>
                )}
              </>
            )}
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            {authType === 'admin' ? (
              <>
                Need help? Contact{' '}
                <a href="mailto:admin@tcards.com" className="text-red-600 hover:text-red-700 font-medium">
                  admin@tcards.com
                </a>
              </>
            ) : isLogin ? (
              <>
                {t('auth_footer_no_account')}{' '}
                <button
                  type="button"
                  onClick={() => setIsLogin(false)}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  {t('auth_footer_create_now')}
                </button>
              </>
            ) : (
              <>
                {t('auth_footer_have_account')}{' '}
                <button
                  type="button"
                  onClick={() => setIsLogin(true)}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  {t('auth_footer_sign_in')}
                </button>
              </>
            )}
          </p>
          <div className="mt-4">
            <button
              type="button"
              onClick={handleExitSignup}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg border text-gray-700 hover:bg-gray-50"
            >
              {isLogin ? t('auth_exit_signin') : t('auth_exit_signup')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
